import React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AdminRideSummaryDto,
  AdminUserBookingDto,
  AdminUserDetailsDto,
  AdminUserPayoutDto,
  AdminUserReviewDto,
  AdminUserRideRequestDto,
  AdminUserUsageEventDto,
  getAdminUser,
  getAdminUserBookings,
  getAdminUserDriverRides,
  getAdminUserPayouts,
  getAdminUserReviews,
  getAdminUserRideRequests,
  getAdminUserTransactions,
  getAdminUserUsageEvents,
  PageDto,
  PaymentMethodDto,
  useApiClient,
  UserTransactionHistoryItemDto,
} from '../api/client';
import { InfoTile, MetricCard, PageHeader, PageShell, PaginationBar, StatusBadge, toneForPaymentStatus, toneForRideStatus } from '../components/ui';
import { useToast } from '../toast/ToastProvider';
import { formatDateTime } from '../utils/dateFormat';

const DEFAULT_PAGE_SIZE = 10;

type TabKey = 'profile' | 'usage' | 'ratings' | 'transactions' | 'payouts' | 'driver-rides' | 'bookings' | 'requests' | 'verification';

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: 'profile', label: 'Profile' },
  { key: 'usage', label: 'Usage & searches' },
  { key: 'ratings', label: 'Ratings' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'payouts', label: 'Payouts' },
  { key: 'driver-rides', label: 'Driver rides' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'requests', label: 'Ride requests' },
  { key: 'verification', label: 'Verification' },
];

function formatName(user?: { email?: string | null; firstName?: string | null; lastName?: string | null } | null) {
  if (!user) return 'Unknown user';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || 'Unknown user';
}

function formatMaybeDate(value?: string | null) {
  return value ? formatDateTime(value) : '-';
}

function formatMoney(cents?: number | null) {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100);
}

function copyText(value?: string | null) {
  if (!value) return;
  void navigator.clipboard?.writeText(value);
}

function stars(value?: number | null) {
  if (value == null) return '-';
  return `${'★'.repeat(Math.max(0, Math.min(5, value)))}${'☆'.repeat(Math.max(0, 5 - value))}`;
}

function isVerificationApproved(status?: string | null) {
  const normalized = (status || '').toUpperCase();
  return normalized === 'VERIFIED' || normalized === 'VERIFIED_DRIVER' || normalized === 'VALID' || normalized === 'APPROVED';
}

function isExpiredDate(value?: string | null) {
  if (!value) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expires = new Date(`${value}T00:00:00`);
  return !Number.isNaN(expires.getTime()) && expires < today;
}

function verificationClasses(status?: string | null, expirationDate?: string | null) {
  return isVerificationApproved(status) && !isExpiredDate(expirationDate)
    ? 'bg-green-50 text-green-800 ring-green-200'
    : 'bg-red-50 text-red-800 ring-red-200';
}

export const AdminUserDetailsPage: React.FC = () => {
  const { userId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabKey) || 'profile';
  const { request } = useApiClient();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const [user, setUser] = React.useState<AdminUserDetailsDto | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    if (!userId) return;
    setLoading(true);
    getAdminUser(request, userId)
      .then(setUser)
      .catch((err) => showError((err as Error)?.message || 'Failed to load user'))
      .finally(() => setLoading(false));
  }, [request, showError, userId]);

  function setTab(tab: TabKey) {
    setSearchParams({ tab });
  }

  async function deleteAccount() {
    if (!user || user.deletedAt || loading || deleting) return;
    if (!window.confirm(
      `Konto von ${formatName(user)} (${user.email || user.id}) endgültig löschen?\n\n` +
      'Das Konto wird wie in der App gelöscht bzw. anonymisiert. Zukünftige Fahrten und Buchungen werden storniert, Serien pausiert. Diese Aktion kann nicht rückgängig gemacht werden.'
    )) return;
    setDeleting(true);
    try {
      await request<void>(`/admin/users/${encodeURIComponent(user.id)}`, { method: 'DELETE' });
      showSuccess('Konto gelöscht.');
      navigate('/users', { replace: true });
    } catch (err) {
      showError((err as Error)?.message || 'Konto konnte nicht gelöscht werden.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <PageShell>
      {loading && !user && <div className="admin-panel p-6 text-sm text-gray-500">Loading user...</div>}
      {user && (
        <>
          <UserHeader user={user} deleting={deleting} deleteDisabled={loading || deleting} onDelete={deleteAccount} />
          <EmailPreference
            user={user}
            loading={loading || deleting}
            onOptOut={async () => {
              if (!window.confirm('Alle optionalen E-Mails für diesen Nutzer abbestellen?')) return;
              setLoading(true);
              try {
                const preference = await request<Pick<AdminUserDetailsDto, 'optionalEmailOptedOutAt' | 'optionalEmailOptOutSource' | 'optionalEmailOptOutBy'>>(`/admin/users/${encodeURIComponent(user.id)}/email-opt-out`, { method: 'POST' });
                setUser((current) => current ? { ...current, ...preference } : current);
              } catch (err) {
                showError((err as Error).message);
              } finally {
                setLoading(false);
              }
            }}
          />
          <nav className="admin-user-tabs" aria-label="User details sections">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className="admin-user-tab"
                aria-current={activeTab === tab.key ? 'page' : undefined}
                onClick={() => setTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
          <div className="admin-user-tab-content">
            {activeTab === 'profile' && <ProfileTab user={user} />}
            {activeTab === 'usage' && <UsageTab user={user} />}
            {activeTab === 'ratings' && <RatingsTab userId={user.id} />}
            {activeTab === 'transactions' && <TransactionsTab userId={user.id} />}
            {activeTab === 'payouts' && <PayoutsTab user={user} />}
            {activeTab === 'driver-rides' && <DriverRidesTab userId={user.id} />}
            {activeTab === 'bookings' && <BookingsTab userId={user.id} />}
            {activeTab === 'requests' && <RideRequestsTab userId={user.id} />}
            {activeTab === 'verification' && <VerificationTab user={user} />}
          </div>
        </>
      )}
    </PageShell>
  );
};

function UserHeader({ user, deleting, deleteDisabled, onDelete }: {
  user: AdminUserDetailsDto;
  deleting: boolean;
  deleteDisabled: boolean;
  onDelete: () => void;
}) {
  const displayName = formatName(user);
  return (
    <section className="admin-user-overview">
      <div className="admin-user-identity-panel">
        <Link className="admin-user-back-link" to="/users">← Back to users</Link>
        <div className="admin-user-identity-row">
          {user.profilePictureUrl ? (
            <img src={user.profilePictureUrl} alt="" className="admin-user-detail-avatar" />
          ) : (
            <div className="admin-user-detail-avatar admin-user-detail-avatar-fallback" aria-hidden="true">
              {displayName.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="admin-user-identity-copy">
            <PageHeader
              title={displayName}
              description={user.email || 'No email'}
              actions={<>
                <Link className="btn btn-neutral" to={`/rides?${new URLSearchParams({ userId: user.id, userName: displayName })}`}>View rides</Link>
                {!user.deletedAt && <button type="button" className="btn btn-danger" disabled={deleteDisabled} onClick={onDelete}>
                  {deleting ? 'Konto wird gelöscht…' : 'Konto löschen'}
                </button>}
              </>}
              meta={<StatusBadge tone={user.deletedAt ? 'red' : 'green'}>{user.deletedAt ? `Deleted ${formatMaybeDate(user.deletedAt)}` : 'Active'}</StatusBadge>}
            />
            <div className="admin-user-copy-actions">
              <button className="btn btn-ghost" onClick={() => copyText(user.id)}>Copy user ID</button>
              {user.stripeCustomerId && <button className="btn btn-ghost" onClick={() => copyText(user.stripeCustomerId)}>Copy Stripe ID</button>}
              {user.externalAuthSubject && <button className="btn btn-ghost" onClick={() => copyText(user.externalAuthSubject)}>Copy Auth0 ID</button>}
            </div>
          </div>
        </div>
      </div>
      <div className="admin-user-metrics">
        <MetricCard label="Rating" value={user.averageRating != null ? user.averageRating.toFixed(1) : '—'} tone="violet" />
        <MetricCard label="Reviews received / given" value={`${user.receivedReviewCount || 0} / ${user.givenReviewCount || 0}`} tone="slate" />
        <MetricCard label="Pending payout" value={formatMoney(user.payout.pendingAmountInCents)} tone="blue" />
        <MetricCard label="Last seen" value={formatMaybeDate(user.lastSeenAt)} tone="slate" />
      </div>
      <div className="admin-user-overview-meta">
        <span>Joined {formatMaybeDate(user.createdAt)}</span>
        <span>Verification: {user.verificationStatus || 'NOT_VERIFIED'}</span>
        <span>Payout: {user.payout.verificationStatus || '—'}</span>
      </div>
    </section>
  );
}

function EmailPreference({ user, loading, onOptOut }: { user: AdminUserDetailsDto; loading: boolean; onOptOut: () => void }) {
  const optedOut = Boolean(user.optionalEmailOptedOutAt);
  return (
    <section className="admin-user-email-preference" aria-labelledby="email-preference-title">
      <div className="admin-user-email-copy">
        <div className="admin-user-email-heading">
          <h2 id="email-preference-title">Optionale E-Mails</h2>
          <StatusBadge tone={optedOut ? 'amber' : 'green'}>{optedOut ? 'Abgemeldet' : 'Aktiv'}</StatusBadge>
        </div>
        <p>{optedOut
          ? `Abgemeldet seit ${formatMaybeDate(user.optionalEmailOptedOutAt)}${user.optionalEmailOptOutSource ? ` · ${user.optionalEmailOptOutSource}` : ''}`
          : 'Kein globales Opt-out. Kategorie-Einstellungen und Provider-Sperren werden zusätzlich berücksichtigt.'}</p>
        {user.optionalEmailOptOutBy && <span>Ausgeführt von: {user.optionalEmailOptOutBy}</span>}
      </div>
      {!optedOut && <button className="btn btn-neutral" disabled={loading} onClick={onOptOut}>Manuell abmelden</button>}
    </section>
  );
}

function ProfileTab({ user }: { user: AdminUserDetailsDto }) {
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Info label="Phone" value={user.phone || '-'} />
        <Info label="Date of birth" value={user.dateOfBirth || '-'} />
        <Info label="Language" value={user.preferredLanguage || '-'} />
        <Info label="Push token" value={user.hasFcmToken ? 'Yes' : 'No'} />
        <Info label="Stripe customer" value={user.stripeCustomerId || '-'} />
        <Info label="External auth" value={user.externalAuthSubject || '-'} />
        <Info label="Last seen" value={formatMaybeDate(user.lastSeenAt)} />
        <Info label="AGB accepted" value={formatMaybeDate(user.agbAcceptedAt)} />
        <Info label="Onboarding completed" value={formatMaybeDate(user.onboardingCompletedAt)} />
        <Info label="Onboarding version" value={String(user.onboardingVersion ?? '-')} />
        <Info label="Acquisition source" value={user.onboardingAcquisitionSource || '-'} />
        <Info label="Source detail" value={user.onboardingOtherSource || '-'} />
        <Info label="Acquisition event" value={user.onboardingEventName || '-'} />
        <Info label="Event date" value={formatMaybeDate(user.onboardingEventStartTime)} />
        <Info label="Event location" value={user.onboardingEventLocationName || '-'} />
        <Info label="Acquisition community" value={user.onboardingCommunityName || '-'} />
        <Info label="Acquisition community ID" value={user.onboardingCommunityId || '-'} />
        <Info label="Mobility role" value={user.onboardingMobilityRole || '-'} />
        <Info label="Verification choice" value={user.onboardingVerificationChoice || '-'} />
        <Info label="Onboarding place" value={[user.onboardingPlaceName, user.onboardingRegionName, user.onboardingCountryCode].filter(Boolean).join(', ') || '-'} />
        <Info label="Location captured" value={formatMaybeDate(user.onboardingLocationCapturedAt)} />
        <Info label="Anonymized" value={formatMaybeDate(user.anonymizedAt)} />
        <Info label="Driver motivation" value={user.driverDetourMotivation || '-'} />
        <Info label="Rider motivation" value={user.riderDetourMotivation || '-'} />
        <Info label="Payout pending" value={formatMoney(user.payout.pendingAmountInCents)} />
        <Info label="IBAN" value={user.payout.hasIban ? `****${user.payout.ibanLast4 || ''}` : 'Missing'} />
      </div>
      <Section title="Payment methods">
        {user.paymentMethodsError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{user.paymentMethodsError}</div>
        ) : user.paymentMethods.length > 0 ? (
          <div className="grid gap-2 md:grid-cols-2">
            {user.paymentMethods.map((method) => (
              <div key={method.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                <div className="font-semibold text-gray-900">{formatPaymentMethod(method)}</div>
                <button className="mt-1 font-mono text-xs text-gray-500 hover:text-gray-900" onClick={() => copyText(method.id)}>{method.id}</button>
              </div>
            ))}
          </div>
        ) : (
          <Empty>No payment method.</Empty>
        )}
      </Section>
      <Section title="Profile text and groups">
        {user.description && <p className="mb-4 whitespace-pre-wrap text-sm text-gray-700">{user.description}</p>}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase text-gray-500">Interests</div>
            <div className="flex flex-wrap gap-2">
              {user.interests.map((interest) => (
                <span key={interest.id} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                  {interest.nameDe || interest.nameEn || interest.name || interest.id}
                  {interest.type && <span className="font-normal text-gray-500"> - {interest.typeNameDe || interest.typeNameEn || interest.type}</span>}
                </span>
              ))}
              {user.interests.length === 0 && <Empty>No interests.</Empty>}
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs font-semibold uppercase text-gray-500">Communities</div>
            <div className="grid gap-2">
              {user.communities.map((community) => (
                <div key={community.membershipId} className="rounded-lg border border-gray-200 p-3 text-sm">
                  <div className="font-semibold text-gray-900">{community.name}</div>
                  <div className="text-xs text-gray-500">{community.type || '-'} - joined {formatMaybeDate(community.joinedAt)}</div>
                </div>
              ))}
              {user.communities.length === 0 && <Empty>No active communities.</Empty>}
            </div>
          </div>
        </div>
      </Section>
      <Section title="Community groups">
        <div className="grid gap-2 md:grid-cols-2">
          {user.groups.map((group) => (
            <div key={group.id} className="rounded-lg border border-gray-200 p-3 text-sm">
              <div className="font-semibold text-gray-900">{group.name}</div>
              <div className="text-xs text-gray-500">{group.communityName}</div>
            </div>
          ))}
          {user.groups.length === 0 && <Empty>No community groups.</Empty>}
        </div>
      </Section>
      <Section title="Emergency contacts">
        <div className="grid gap-2 md:grid-cols-2">
          {user.emergencyContacts.map((contact) => (
            <div key={contact.id} className="rounded-lg border border-red-100 bg-red-50/40 p-3 text-sm">
              <div className="font-semibold text-gray-900">{contact.firstName} {contact.lastName}</div>
              <div className="mt-1 text-gray-700">{contact.phoneNumber}</div>
              <div className="mt-1 text-xs text-gray-500">Added {formatMaybeDate(contact.createdAt)}</div>
            </div>
          ))}
          {user.emergencyContacts.length === 0 && <Empty>No emergency contacts.</Empty>}
        </div>
      </Section>
      <Section title="Vehicles">
        <div className="grid gap-2 md:grid-cols-2">
          {user.vehicles.map((vehicle) => (
            <div key={vehicle.id} className={`rounded-lg border p-3 text-sm ${vehicle.deleted ? 'border-red-300 bg-red-50 ring-1 ring-red-200' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className={`font-semibold ${vehicle.deleted ? 'text-red-950 line-through decoration-red-500 decoration-2' : 'text-gray-900'}`}>{[vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' ') || vehicle.id}</div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ring-1 ${vehicle.deleted ? 'bg-red-100 text-red-800 ring-red-200' : 'bg-green-50 text-green-800 ring-green-200'}`}>
                  {vehicle.deleted ? 'Deleted' : 'Active'}
                </span>
              </div>
              <div className={vehicle.deleted ? 'mt-1 text-red-800' : 'mt-1 text-gray-500'}>{vehicle.licensePlate || 'No plate'} - {vehicle.color || 'No color'} - {vehicle.seats || 0} seats</div>
              <div className={vehicle.deleted ? 'mt-1 text-xs font-semibold text-red-700' : 'mt-1 text-xs text-gray-500'}>{vehicle.isDefault ? 'Default - ' : ''}{vehicle.deleted ? 'Deleted vehicle, do not use for new rides' : 'Available for rides'}</div>
            </div>
          ))}
          {user.vehicles.length === 0 && <Empty>No vehicles.</Empty>}
        </div>
      </Section>
    </div>
  );
}

function UsageTab({ user }: { user: AdminUserDetailsDto }) {
  const [searchesOnly, setSearchesOnly] = React.useState(true);
  const usage = user.usage;
  const device = usage.latestDevice;
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Info label="Searches" value={String(usage.searchCount)} />
        <Info label="Zero-result searches" value={String(usage.zeroResultSearchCount)} />
        <Info label="Average hits" value={usage.averageResultCount != null ? usage.averageResultCount.toFixed(1) : '-'} />
        <Info label="Failed tracking events" value={String(usage.failedSearchCount)} />
        <Info label="App sessions" value={String(usage.appSessionCount)} />
        <Info label="App sessions · 7 days" value={String(usage.appSessionCountLast7Days)} />
        <Info label="App sessions · 30 days" value={String(usage.appSessionCountLast30Days)} />
        <Info label="Active days" value={String(usage.activeDayCount)} />
        <Info label="Last app session" value={formatMaybeDate(usage.lastAppSessionAt)} />
        <Info label="Saved locations" value={String(usage.savedLocationCount)} />
        <Info label="Last usage" value={formatMaybeDate(usage.lastUsageAt)} />
      </div>
      <Section title="Latest app device">
        {device ? (
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
            <Info label="Platform" value={device.platform || '-'} />
            <Info label="Device" value={[device.manufacturer, device.model].filter(Boolean).join(' ') || '-'} />
            <Info label="OS version" value={device.osVersion || '-'} />
            <Info label="App version" value={[device.appVersion, device.appBuildNumber && `build ${device.appBuildNumber}`].filter(Boolean).join(' · ') || '-'} />
            <Info label="Installation ID" value={device.installationId || '-'} />
            <Info label="Emulator" value={device.emulator == null ? '-' : device.emulator ? 'Yes' : 'No'} />
            <Info label="Last reported" value={formatMaybeDate(device.lastSeenAt)} />
          </div>
        ) : (
          <Empty>No device metadata reported yet.</Empty>
        )}
      </Section>
      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-blue-900">
        App sessions are recorded once when an authenticated app enters the foreground. Pending-review polling is not counted.
      </div>
      <PagedTab<AdminUserUsageEventDto>
        load={(request, page, size) => getAdminUserUsageEvents(request, user.id, { page, size, searchesOnly })}
        depKey={`${user.id}:${searchesOnly}`}
        renderControls={(
          <select className="form-control max-w-[180px]" value={searchesOnly ? 'searches' : 'all'} onChange={(event) => setSearchesOnly(event.target.value === 'searches')}>
            <option value="searches">Searches</option>
            <option value="all">All usage events</option>
          </select>
        )}
        headers={['Event', 'Route / context', 'Requested', 'Result']}
        renderRow={(event) => (
          <tr key={event.id} className="border-t align-top">
            <td className="px-3 py-2">
              <div className="font-semibold text-gray-900">{
                event.eventType === 'RIDE_SEARCH'
                  ? 'Ride search'
                  : event.eventType === 'APP_SESSION'
                    ? 'App session'
                    : 'Legacy pending-review poll'
              }</div>
              <div className="text-xs text-gray-500">{formatMaybeDate(event.occurredAt)} · {event.status}</div>
            </td>
            <td className="max-w-lg px-3 py-2">
              {event.eventType === 'RIDE_SEARCH' ? (
                <><div>{event.departureName || event.departureAddress || '-'}</div><div className="text-xs text-gray-500">to {event.destinationName || event.destinationAddress || '-'}</div></>
              ) : <span className="text-gray-500">Reviews dashboard</span>}
            </td>
            <td className="px-3 py-2">{formatMaybeDate(event.requestedTime)}<div className="text-xs text-gray-500">{event.seatsRequested != null ? `${event.seatsRequested} seats` : '-'}</div></td>
            <td className="px-3 py-2">
              {event.eventType === 'RIDE_SEARCH' ? <span className="font-semibold">{event.resultCount ?? '-'} hits</span> : <span>{event.pendingReviewCount ?? 0} reviews</span>}
              {event.failureType && <div className="text-xs text-red-700">{event.failureType}</div>}
            </td>
          </tr>
        )}
        empty="No usage recorded yet."
      />
    </div>
  );
}

function formatPaymentMethod(method: PaymentMethodDto) {
  const brand = method.brand ? method.brand.charAt(0).toUpperCase() + method.brand.slice(1).toLowerCase() : 'Card';
  const last4 = method.last4 ? `**** ${method.last4}` : 'No last four';
  const expiry = method.expMonth && method.expYear ? ` - ${String(method.expMonth).padStart(2, '0')}/${method.expYear}` : '';
  return `${brand} ${last4}${expiry}`;
}

function RatingsTab({ userId }: { userId: string }) {
  const [direction, setDirection] = React.useState<'received' | 'given'>('received');
  return (
    <PagedTab<AdminUserReviewDto>
      load={(request, page, size) => getAdminUserReviews(request, userId, { page, size, direction })}
      depKey={`${userId}:${direction}`}
      renderControls={<div className="mb-3 flex gap-2">
        {(['received', 'given'] as const).map((value) => (
          <button key={value} className={`btn ${direction === value ? 'btn-primary' : 'btn-neutral'}`} onClick={() => setDirection(value)}>
            {value === 'received' ? 'Received' : 'Given'}
          </button>
        ))}
      </div>}
      headers={['Rating', 'Counterparty', 'Ride', 'Comment']}
      renderRow={(review) => {
        const counterparty = direction === 'received' ? review.reviewer : review.reviewee;
        return (
          <tr key={review.id} className="border-t align-top">
            <td className="px-3 py-2 font-semibold text-amber-600">{review.discardedAt ? 'Discarded' : stars(review.starRating)}</td>
            <td className="px-3 py-2">{formatName(counterparty)}<button className="block font-mono text-xs text-gray-500" onClick={() => copyText(counterparty.id)}>{counterparty.id}</button></td>
            <td className="px-3 py-2"><Link className="font-semibold hover:underline" to={`/rides/${review.rideId}`}>{formatMaybeDate(review.rideTime)}</Link><div className="text-xs text-gray-500">{review.departure.publicName} to {review.destination.publicName}</div></td>
            <td className="px-3 py-2 max-w-xl">{review.comment || '-'}</td>
          </tr>
        );
      }}
      empty="No ratings."
    />
  );
}

function TransactionsTab({ userId }: { userId: string }) {
  return (
    <PagedTab<UserTransactionHistoryItemDto>
      load={(request, page, size) => getAdminUserTransactions(request, userId, { page, size })}
      depKey={userId}
      headers={['Transaction', 'Amount', 'Counterparty', 'Context']}
      renderRow={(transaction) => (
        <tr key={transaction.id} className="border-t align-top">
          <td className="px-3 py-2"><div className="font-semibold text-gray-900">{transaction.type}</div><div className="text-xs text-gray-500">{formatMaybeDate(transaction.occurredAt)}</div></td>
          <td className={`px-3 py-2 font-semibold ${transaction.direction === 'CREDIT' ? 'text-green-700' : 'text-gray-900'}`}>{transaction.direction === 'CREDIT' ? '+' : '-'}{formatMoney(transaction.amountInCents)}</td>
          <td className="px-3 py-2"><button className="font-mono text-xs text-gray-600 hover:text-gray-900" onClick={() => copyText(transaction.counterpartyUserId)}>{transaction.counterpartyUserId || '-'}</button></td>
          <td className="px-3 py-2"><div>{transaction.status}</div><div className="text-xs text-gray-500">{transaction.description || '-'}</div>{transaction.rideId && <Link className="text-xs font-semibold text-gray-700 hover:underline" to={`/rides/${transaction.rideId}`}>Ride {transaction.rideId}</Link>}</td>
        </tr>
      )}
      empty="No captured payment transactions."
    />
  );
}

function PayoutsTab({ user }: { user: AdminUserDetailsDto }) {
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 md:grid-cols-4">
        <Info label="Account holder" value={user.payout.accountHolderName || '-'} />
        <Info label="Payout eligible" value={user.payout.payoutEligible ? 'Yes' : 'No'} />
        <Info label="Exported" value={formatMoney(user.payout.exportedAmountInCents)} />
        <Info label="Paid" value={formatMoney(user.payout.paidOutAmountInCents)} />
      </div>
      <PagedTab<AdminUserPayoutDto>
        load={(request, page, size) => getAdminUserPayouts(request, user.id, { page, size })}
        depKey={user.id}
        headers={['Payout', 'Period', 'Amount', 'Status']}
        renderRow={(payout) => (
          <tr key={payout.id} className="border-t align-top">
            <td className="px-3 py-2"><button className="font-mono text-xs text-gray-600 hover:text-gray-900" onClick={() => copyText(payout.id)}>{payout.id}</button><div className="text-xs text-gray-500">{payout.exportReference || 'No export ref'}</div></td>
            <td className="px-3 py-2">{formatMaybeDate(payout.periodStart)}<div className="text-xs text-gray-500">to {formatMaybeDate(payout.periodEnd)}</div></td>
            <td className="px-3 py-2">{formatMoney(payout.amountInCents)}<div className="text-xs text-gray-500">{payout.bookingCount || 0} bookings</div></td>
            <td className="px-3 py-2">{payout.status || '-'}{payout.failureReason && <div className="text-xs text-red-700">{payout.failureReason}</div>}</td>
          </tr>
        )}
        empty="No payout records."
      />
    </div>
  );
}

function DriverRidesTab({ userId }: { userId: string }) {
  return (
    <PagedTab<AdminRideSummaryDto>
      load={(request, page, size) => getAdminUserDriverRides(request, userId, { page, size })}
      depKey={userId}
      headers={['Ride', 'Route', 'Status', 'Bookings']}
      renderRow={(ride) => (
        <tr key={ride.id} className="border-t">
          <td className="px-3 py-2"><Link className="font-semibold text-gray-900 hover:underline" to={`/rides/${ride.id}`}>{formatMaybeDate(ride.time)}</Link><div className="font-mono text-xs text-gray-500">{ride.id}</div></td>
          <td className="px-3 py-2">{ride.departure.publicName}<div className="text-xs text-gray-500">to {ride.destination.publicName}</div></td>
          <td className="px-3 py-2"><StatusBadge tone={toneForRideStatus(ride.status)}>{ride.status}</StatusBadge></td>
          <td className="px-3 py-2">{ride.bookingCount} - {ride.approvedSeats}/{ride.numberOfSeats} seats</td>
        </tr>
      )}
      empty="No driver rides."
    />
  );
}

function BookingsTab({ userId }: { userId: string }) {
  return (
    <PagedTab<AdminUserBookingDto>
      load={(request, page, size) => getAdminUserBookings(request, userId, { page, size })}
      depKey={userId}
      headers={['Booking', 'Ride', 'Driver', 'Payment']}
      renderRow={(booking) => (
        <tr key={booking.id} className="border-t align-top">
          <td className="px-3 py-2"><button className="font-mono text-xs text-gray-600 hover:text-gray-900" onClick={() => copyText(booking.id)}>{booking.id}</button><div className="text-xs text-gray-500">{booking.rideRequestStatus || '-'}</div></td>
          <td className="px-3 py-2"><Link className="font-semibold hover:underline" to={`/rides/${booking.rideId}`}>{formatMaybeDate(booking.rideTime)}</Link><div className="text-xs text-gray-500">{booking.departure.publicName} to {booking.destination.publicName}</div></td>
          <td className="px-3 py-2">{formatName(booking.driver)}</td>
          <td className="px-3 py-2"><StatusBadge tone={toneForPaymentStatus(booking.paymentCaptureStatus)}>{booking.paymentCaptureStatus || '-'}</StatusBadge><div className="mt-1 text-xs text-gray-500">{formatMoney(booking.priceInCents)} - fee {formatMoney(booking.serviceFeeInCents)}</div>{booking.paymentCaptureLastError && <div className="text-xs text-red-700">{booking.paymentCaptureLastError}</div>}</td>
        </tr>
      )}
      empty="No rider bookings."
    />
  );
}

function RideRequestsTab({ userId }: { userId: string }) {
  return (
    <PagedTab<AdminUserRideRequestDto>
      load={(request, page, size) => getAdminUserRideRequests(request, userId, { page, size })}
      depKey={userId}
      headers={['Request', 'Route', 'Status', 'Time']}
      renderRow={(rideRequest) => (
        <tr key={rideRequest.id} className="border-t">
          <td className="px-3 py-2"><button className="font-mono text-xs text-gray-600 hover:text-gray-900" onClick={() => copyText(rideRequest.id)}>{rideRequest.id}</button></td>
          <td className="px-3 py-2">{rideRequest.departure.publicName}<div className="text-xs text-gray-500">to {rideRequest.destination.publicName}</div></td>
          <td className="px-3 py-2">{rideRequest.status}</td>
          <td className="px-3 py-2">{formatMaybeDate(rideRequest.time)}</td>
        </tr>
      )}
      empty="No ride requests."
    />
  );
}

function VerificationTab({ user }: { user: AdminUserDetailsDto }) {
  return (
    <Section title="Verification documents">
      <div className="grid gap-2 md:grid-cols-3">
        {user.verificationDocuments.map((document) => (
          <div key={document.id} className={`rounded-lg border p-3 text-sm ring-1 ${verificationClasses(document.status, document.expirationDate)}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="font-semibold text-gray-900">{document.documentType || 'Document'}</div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ring-1 ${verificationClasses(document.status, document.expirationDate)}`}>
                {isVerificationApproved(document.status) && !isExpiredDate(document.expirationDate) ? 'Valid' : 'Invalid'}
              </span>
            </div>
            <div className="mt-1 text-gray-700">{document.status || '-'} - expires {document.expirationDate || '-'}</div>
            {isExpiredDate(document.expirationDate) && <div className="mt-1 text-xs font-semibold text-red-700">Expired document</div>}
          </div>
        ))}
        {user.verificationDocuments.length === 0 && <Empty>No verification documents.</Empty>}
      </div>
    </Section>
  );
}

function PagedTab<T>({
  load,
  depKey,
  headers,
  renderRow,
  empty,
  renderControls,
}: {
  load: (request: (input: RequestInfo, init?: RequestInit) => Promise<any>, page: number, size: number) => Promise<PageDto<T>>;
  depKey: string;
  headers: string[];
  renderRow: (item: T) => React.ReactNode;
  empty: string;
  renderControls?: React.ReactNode;
}) {
  const { request } = useApiClient();
  const { showError } = useToast();
  const [pageNumber, setPageNumber] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = React.useState<PageDto<T> | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setPageNumber(0);
  }, [depKey]);

  React.useEffect(() => {
    setLoading(true);
    load(request, pageNumber, pageSize)
      .then(setPage)
      .catch((err) => showError((err as Error)?.message || 'Failed to load tab'))
      .finally(() => setLoading(false));
  }, [request, showError, load, pageNumber, pageSize, depKey]);

  const rows = page?.content || [];
  return (
    <section className="admin-panel admin-user-paged-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="text-sm text-gray-600">{loading ? 'Loading...' : `${page?.totalElements || 0} records`}</div>
        <div className="flex items-center gap-2">
          {renderControls}
          <select className="form-control max-w-[120px]" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPageNumber(0); }}>
            {[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size} / page</option>)}
          </select>
        </div>
      </div>
      <CompactTable headers={headers}>
        {rows.map(renderRow)}
      </CompactTable>
      {!loading && rows.length === 0 && <div className="p-4"><Empty>{empty}</Empty></div>}
      <PaginationBar
        loading={loading}
        pageNumber={pageNumber}
        pageSize={pageSize}
        rowCount={rows.length}
        totalElements={page?.totalElements}
        isLast={page?.last}
        onPrevious={() => setPageNumber((p) => Math.max(0, p - 1))}
        onNext={() => setPageNumber((p) => p + 1)}
        previousLabel="Previous"
        nextLabel="Next"
      />
    </section>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return <InfoTile label={label} value={value} />;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="admin-panel admin-user-section">
      <h2 className="admin-user-section-title">{title}</h2>
      <div className="admin-user-section-content">{children}</div>
    </section>
  );
}

function CompactTable({ headers, children }: { headers: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="admin-table admin-user-detail-table">
        <thead>
          <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">{children}</div>;
}

export default AdminUserDetailsPage;
